import { useEffect } from 'react';
import { useFetcher, useLocation } from 'react-router';
import {
    ENQUIRY_FORM_API_ROUTE,
    type EnquiryFormApiResponse,
} from '~/routes/remix-api.enquiry-form';
import { useFeature } from '~/modules/root/hook/use-feature';
import { EnquiryFormPageType, EnquiryFormTrigger } from 'generated/graphql';
import {
    PAGE_TYPE_BY_ITEM_TYPE,
    type PublicEnquiryForm,
} from '../types/public-enquiry-form';
import type { UseEnquiryProps } from './use-enquiry';

export interface UseEnquiryFormForPageOptions {
    professionalId: number | null | undefined;
    /**
     * The Forms page type. Defaults to the page the visitor is on (from the
     * route), then to `itemType`.
     */
    pageType?: EnquiryFormPageType;
    /**
     * The enquiry's library item type. Only used as the page-type fallback
     * off a recognised public route, since e.g. a project page's "Message the
     * professional" CTA still belongs to the Project cell.
     */
    itemType?: UseEnquiryProps['itemType'];
    trigger?: EnquiryFormTrigger;
    /**
     * The product a product page shows. Its own choice for the button (a
     * chosen form, or hidden) wins over the Product page type's form; ignored
     * on other page types.
     */
    productId?: number | null;
    /** Skip the lookup entirely (e.g. the caller already has a form). */
    enabled?: boolean;
}

export interface EnquiryFormForPage {
    /** True until the lookup has answered. Always false when Forms is off. */
    isLoading: boolean;
    /** The professional switched this CTA off for this page type. */
    isOff: boolean;
    /** The configured form, or null under legacy / off. */
    form: PublicEnquiryForm | null;
    status: EnquiryFormApiResponse['status'];
}

const LEGACY: EnquiryFormForPage = {
    isLoading: false,
    isOff: false,
    form: null,
    status: 'legacy',
};

const PAGE_TYPE_BY_ROUTE_PREFIX: ReadonlyArray<
    readonly [prefix: string, pageType: EnquiryFormPageType]
> = [
    ['/project/', EnquiryFormPageType.Project],
    ['/product/', EnquiryFormPageType.Product],
    ['/professional/', EnquiryFormPageType.Professional],
    ['/article/', EnquiryFormPageType.Article],
];

/** The Forms page type of a public detail route, if it is one. */
export function pageTypeFromPath(
    pathname: string,
): EnquiryFormPageType | undefined {
    return PAGE_TYPE_BY_ROUTE_PREFIX.find(([prefix]) =>
        pathname.startsWith(prefix),
    )?.[1];
}

/**
 * The lookup URL for a page's CTA form. A product ID is only sent for
 * product pages, where the product's own choice for the button can win.
 */
export function enquiryFormForPageUrl({
    professionalId,
    pageType,
    trigger,
    productId,
}: {
    professionalId: number;
    pageType: EnquiryFormPageType | '';
    trigger: EnquiryFormTrigger;
    productId?: number | null;
}): string {
    const params = new URLSearchParams({
        professionalId: String(professionalId),
        pageType,
        trigger,
    });
    if (pageType === EnquiryFormPageType.Product && productId) {
        params.set('productId', String(productId));
    }
    return `${ENQUIRY_FORM_API_ROUTE}?${params.toString()}`;
}

/**
 * Resolves the enquiry form a public page's CTA opens, once Forms is on.
 * Fetches lazily so page loaders and their caches are untouched; callers keep
 * their flat-field behaviour until the answer lands and whenever the flag is
 * off.
 */
export function useEnquiryFormForPage({
    professionalId,
    pageType,
    itemType,
    trigger = EnquiryFormTrigger.Enquire,
    productId,
    enabled = true,
}: UseEnquiryFormForPageOptions): EnquiryFormForPage {
    const isFormsEnabled = useFeature('bm_forms');
    const { pathname } = useLocation();
    const resolvedPageType =
        pageType ??
        pageTypeFromPath(pathname) ??
        (itemType ? PAGE_TYPE_BY_ITEM_TYPE[itemType] : undefined);
    const resolvedProductId =
        resolvedPageType === EnquiryFormPageType.Product && productId
            ? productId
            : null;
    const fetcher = useFetcher<EnquiryFormApiResponse>({
        key: `enquiry-form:${professionalId ?? 0}:${resolvedPageType ?? ''}:${trigger}:${resolvedProductId ?? 0}`,
    });
    const shouldFetch =
        enabled && isFormsEnabled && !!professionalId && !!resolvedPageType;

    useEffect(() => {
        if (!shouldFetch || fetcher.data || fetcher.state !== 'idle') {
            return;
        }
        fetcher.load(
            enquiryFormForPageUrl({
                professionalId: Number(professionalId),
                pageType: resolvedPageType ?? '',
                trigger,
                productId: resolvedProductId,
            }),
        );
        // The fetcher's state changes must not restart the load.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        shouldFetch,
        professionalId,
        resolvedPageType,
        trigger,
        resolvedProductId,
    ]);

    if (!shouldFetch) {
        return LEGACY;
    }
    if (!fetcher.data) {
        return { ...LEGACY, isLoading: true };
    }

    return {
        isLoading: false,
        isOff: fetcher.data.status === 'off',
        form: fetcher.data.form,
        status: fetcher.data.status,
    };
}
